import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AdminAccessDenied from "../components/admin/AdminAccessDenied";
import {
  adminListWhispers, adminCreateWhisper, adminUpdateWhisper, adminDeleteWhisper,
  adminVaultNotify, formatApiErrorDetail,
} from "../lib/api";
import { Reveal } from "../components/Motion";
import { Trash2 } from "lucide-react";

const DIMS = ["02", "03", "04"];
const INTENSITIES = ["subtle", "standard", "strong"];
const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2.5 text-sm text-white transition-colors";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-white/40 block mb-2";

function WhisperForm({ onCreated }) {
  const [form, setForm] = useState({
    dimension: "02", phrase: "", start_at: "", end_at: "",
    location: "dimensions", intensity: "subtle", active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phrase.trim() || !form.start_at) return toast.error("Phrase and start time are required.");
    setBusy(true);
    try {
      await adminCreateWhisper({
        dimension: form.dimension,
        phrase: form.phrase.trim(),
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        location: form.location || "dimensions",
        intensity: form.intensity,
        active: form.active,
      });
      toast.success("Whisper scheduled.");
      setForm((f) => ({ ...f, phrase: "", start_at: "", end_at: "" }));
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to schedule.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/10 p-8 md:p-10" data-testid="whisper-form">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-8">SCHEDULE A WHISPER</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <div className="md:col-span-2">
          <label className={labelCls}>OFFICIAL PHRASE</label>
          <input value={form.phrase} onChange={(e) => set("phrase", e.target.value)} maxLength={120}
            placeholder="The exact phrase to plant" className={inputCls} data-testid="whisper-phrase" />
        </div>
        <div>
          <label className={labelCls}>DIMENSION</label>
          <select value={form.dimension} onChange={(e) => set("dimension", e.target.value)}
            className={`${inputCls} bg-[#050505]`} data-testid="whisper-dimension">
            {DIMS.map((d) => <option key={d} value={d}>DIMENSION {d}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>INTENSITY</label>
          <select value={form.intensity} onChange={(e) => set("intensity", e.target.value)}
            className={`${inputCls} bg-[#050505]`} data-testid="whisper-intensity">
            {INTENSITIES.map((i) => <option key={i} value={i}>{i.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>START</label>
          <input type="datetime-local" value={form.start_at} onChange={(e) => set("start_at", e.target.value)}
            className={inputCls} data-testid="whisper-start" />
        </div>
        <div>
          <label className={labelCls}>END (OPTIONAL)</label>
          <input type="datetime-local" value={form.end_at} onChange={(e) => set("end_at", e.target.value)}
            className={inputCls} data-testid="whisper-end" />
        </div>
        <div>
          <label className={labelCls}>DISPLAY LOCATION</label>
          <input value={form.location} onChange={(e) => set("location", e.target.value)}
            className={inputCls} data-testid="whisper-location" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-white/60 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
              className="accent-[#d4af37]" data-testid="whisper-active" />
            ACTIVE
          </label>
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="whisper-submit"
        className="mt-9 bg-gold text-black font-mono text-[10px] tracking-[0.25em] px-8 py-3.5 hover:bg-white transition-colors disabled:opacity-50">
        {busy ? "PLANTING..." : "PLANT WHISPER"}
      </button>
    </form>
  );
}

function VaultNotifyPanel() {
  const [digitalId, setDigitalId] = useState("dig-dimension-i-i-am");
  const [assetId, setAssetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dryRun) => {
    if (!digitalId.trim() || !assetId.trim()) return toast.error("Release and asset IDs are required.");
    setBusy(true);
    setResult(null);
    try {
      const res = await adminVaultNotify({ digitalId: digitalId.trim(), assetId: assetId.trim(), dryRun });
      setResult(res);
      toast.success(dryRun ? "Dry run complete — nothing sent." : `Sent ${res.sent} notification(s).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Notify failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 p-8 md:p-10 mt-10" data-testid="vault-notify-panel">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-3">VAULT NOTIFICATIONS</p>
      <p className="text-white/40 text-sm max-w-xl mb-8">
        Notify entitled account owners that an unlocked asset has entered their vault.
        Sends once per owner per asset — duplicates are skipped automatically. Use dry run first.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        <div>
          <label className={labelCls}>DIGITAL RELEASE ID</label>
          <input value={digitalId} onChange={(e) => setDigitalId(e.target.value)} className={inputCls} data-testid="notify-digital-id" />
        </div>
        <div>
          <label className={labelCls}>ASSET ID (e.g. iam-artwork)</label>
          <input value={assetId} onChange={(e) => setAssetId(e.target.value)} placeholder="iam-artwork" className={inputCls} data-testid="notify-asset-id" />
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mt-9">
        <button onClick={() => run(true)} disabled={busy} data-testid="notify-dry-run"
          className="border border-white/25 text-white/70 font-mono text-[10px] tracking-[0.25em] px-7 py-3.5 hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
          DRY RUN
        </button>
        <button onClick={() => run(false)} disabled={busy} data-testid="notify-send"
          className="bg-gold text-black font-mono text-[10px] tracking-[0.25em] px-7 py-3.5 hover:bg-white transition-colors disabled:opacity-50">
          SEND NOTIFICATIONS
        </button>
      </div>
      {result && (
        <p className="font-mono text-[10px] tracking-[0.15em] text-white/50 mt-6 leading-relaxed" data-testid="notify-result">
          {result.dry_run ? "DRY RUN — " : ""}ENTITLED: {result.entitled_emails} · WOULD SEND/SENT: {result.sent} ·
          NO ACCOUNT: {result.skipped_no_account} · OPTED OUT: {result.skipped_opted_out} ·
          ALREADY NOTIFIED: {result.skipped_duplicates} · FAILED: {result.failed}
        </p>
      )}
    </div>
  );
}

export default function AdminWhispers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [whispers, setWhispers] = useState([]);

  const refresh = useCallback(() => {
    adminListWhispers().then(setWhispers).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Signal Control — Motion & Meaning";
  }, []);

  useEffect(() => {
    if (user === false) navigate("/login?next=/admin/whispers", { replace: true });
    if (user && user.role === "admin") refresh();
  }, [user, navigate, refresh]);

  if (!user) return <div className="min-h-screen" />;
  if (user.role !== "admin") return <AdminAccessDenied next="/admin/whispers" />;

  const toggle = async (w) => {
    try {
      await adminUpdateWhisper(w.id, { active: !w.active });
      refresh();
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Remove whisper "${w.phrase}"?`)) return;
    try {
      await adminDeleteWhisper(w.id);
      toast.success("Whisper removed.");
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-whispers-page">
      <div>
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">SIGNAL CONTROL</p>
          <h1 className="font-serif text-3xl md:text-4xl font-light leading-none text-white">
            Whisper Scheduler
          </h1>
          <p className="text-white/50 max-w-xl mt-8 leading-relaxed">
            Plant official phrases into unresolved Dimensions. Only phrases entered here ever
            surface — when nothing is scheduled, the standard unresolved-signal states show instead.
          </p>
        </Reveal>

        <div className="mt-16">
          <Reveal>
            <WhisperForm onCreated={refresh} />
          </Reveal>
        </div>

        <div className="mt-16">
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-6">SCHEDULED WHISPERS</p>
          </Reveal>
          {whispers.length === 0 && (
            <p className="font-serif text-xl text-white/35 border-t border-white/10 py-8">
              Nothing scheduled. The signals remain unresolved.
            </p>
          )}
          {whispers.map((w) => (
            <div key={w.id} className="border-t border-white/10 py-6 grid grid-cols-12 items-center gap-4" data-testid={`whisper-row-${w.id}`}>
              <div className="col-span-12 md:col-span-5">
                <p className="font-serif text-xl text-white/90">{w.phrase}</p>
                <p className="font-mono text-[9px] tracking-[0.2em] text-white/35 mt-2">
                  DIMENSION {w.dimension} · {w.location.toUpperCase()} · {w.intensity.toUpperCase()}
                </p>
              </div>
              <div className="col-span-6 md:col-span-3 font-mono text-[9px] tracking-[0.15em] text-white/40 leading-relaxed">
                {new Date(w.start_at).toLocaleString()}<br />
                {w.end_at ? `→ ${new Date(w.end_at).toLocaleString()}` : "→ NO END"}
              </div>
              <div className="col-span-3 md:col-span-2">
                {w.live ? (
                  <span className="font-mono text-[9px] tracking-[0.25em] text-gold border border-gold/40 px-3 py-1.5">LIVE</span>
                ) : (
                  <span className="font-mono text-[9px] tracking-[0.25em] text-white/30 border border-white/10 px-3 py-1.5">
                    {w.active ? "SCHEDULED" : "INACTIVE"}
                  </span>
                )}
              </div>
              <div className="col-span-3 md:col-span-2 flex items-center justify-end gap-3">
                <button onClick={() => toggle(w)} data-testid={`whisper-toggle-${w.id}`}
                  className="font-mono text-[9px] tracking-[0.2em] border border-white/20 text-white/60 px-4 py-2 hover:border-gold hover:text-gold transition-colors">
                  {w.active ? "DEACTIVATE" : "ACTIVATE"}
                </button>
                <button onClick={() => remove(w)} data-testid={`whisper-delete-${w.id}`} aria-label="Delete whisper"
                  className="text-white/35 hover:text-red-500 transition-colors p-1.5">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20">
          <Reveal>
            <VaultNotifyPanel />
          </Reveal>
        </div>
      </div>
    </div>
  );
}
